import Banner from '@/components/UI/Banner';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { Finding, SendValidationResult } from '@/model/Agent.model';
import { useMemo, useState, type FC } from 'react';
import SendDisclosureList, { type SendDisclosure } from './SendDisclosureList';
import SendFilterChips from './SendFilterChips';
import { toggled } from './SendFilterChips.utils';
import SendFindingsTable from './SendFindingsTable';
import SendOmissionNotice from './SendOmissionNotice';
import SendSectionHeader from './SendSectionHeader';
import { partitionFindings } from '@/components/Agents/SendValidator/utils/SendFindingPartition';
import {
  bySeverityLevel,
  severityChipsOf,
  severityCountsOf,
  severityLevelOf,
} from '@/components/Agents/SendValidator/utils/SendSeverity';
import { aiNotesCount } from '@/components/Agents/SendValidator/utils/SendAiUse';
import { omissionOf } from '@/components/Agents/SendValidator/utils/SendOmission';
import { plural } from '@/components/Agents/SendValidator/utils/SendText';
import { sendDatasetLabel, sendDatasetOf } from '@/components/Agents/SendValidator/utils/SendDomains';
import type { SendFileRuleCheck } from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import styles from './SendResults.module.scss';

interface SendResultsGridProps {
  result: SendValidationResult;
  /** What the rules made of this file, from the server's tally; null on results stored before it. */
  ruleCheck?: SendFileRuleCheck | null;
  /** Rules whose check covers only part of the rule, so their findings carry that label. */
  proxyRules?: ReadonlySet<string>;
  /** Whether Benchling In Vivo AI took part in the run, so a suggestion may be its own. */
  aiUsed?: boolean;
  /**
   * How many uploaded files were confirmed to this file's dataset. The rule tally is the dataset's, so
   * when it is more than one, a rule that failed may have its findings in another of those files.
   */
  datasetFiles?: number;
  /** The section's position in the report, shown before its title. */
  sectionNumber?: number;
  testId?: string;
}

/**
 * The headline for a file nothing was found in. It says the file passed validation only when every
 * rule for it finished with an exact pass and every check decided everything it read; otherwise it
 * says what was not checked, so a partial or missing check never reads as a pass.
 */
const noIssuesHeadline = (datasetName: string, aiNotes: number, ruleCheck?: SendFileRuleCheck | null): string => {
  const aiNotesToReview = `${aiNotesCount(aiNotes)} to review`;
  if (ruleCheck?.exactPass === true) {
    return aiNotes > 0
      ? `No issues found by the validation checks — ${aiNotesToReview}`
      : `No issues found — ${sendDatasetLabel(datasetName)} passed validation`;
  }
  const unchecked = _notNil(ruleCheck)
    ? [
        { count: ruleCheck.notEvaluated, text: plural(ruleCheck.notEvaluated, 'rule', 'rules') + ' not evaluated' },
        {
          count: ruleCheck.checkedPartially,
          text: plural(ruleCheck.checkedPartially, 'rule', 'rules') + ' checked partially',
        },
        {
          count: ruleCheck.undecided,
          text: plural(ruleCheck.undecided, 'check', 'checks') + ' could not decide everything',
        },
      ].reduce<Array<string>>((texts, { count, text }) => {
        if (count > 0) {
          texts.push(text);
        }
        return texts;
      }, [])
    : [];
  const details = aiNotes > 0 ? [...unchecked, aiNotesToReview] : unchecked;
  return _isNotEmpty(details)
    ? `No issues found in the checks that ran — ${details.join(', ')}`
    : 'No issues found in the checks that ran';
};

const bySeverityThenRow = (a: Finding, b: Finding): number => {
  const severityDelta = bySeverityLevel(a, b);
  if (severityDelta !== 0) {
    return severityDelta;
  }
  return (a.row_number ?? 0) - (b.row_number ?? 0);
};

const locationOf = (finding: Finding): string | undefined => {
  const parts = [
    _notNil(finding.column_name) ? finding.column_name : null,
    _notNil(finding.row_number) ? `row ${finding.row_number}` : null,
  ].filter((part): part is string => _notNil(part));
  return _isNotEmpty(parts) ? parts.join(', ') : undefined;
};

/**
 * One file's section of a SEND report: its dataset and rows, the server's own numbers for it, a
 * headline keyed on the submission-blocking count, and the checks' findings in a table filtered by
 * severity. A check's notice that it could not decide something is listed apart, drawn neutrally and
 * never filtered or counted as a finding. Benchling In Vivo AI notes are not shown here: they have
 * their own section, and this one only says how many there are.
 *
 * Every number reads the server's exact counts, never the findings the result carries, and the
 * omission notice announces whatever a limit left out.
 */
const SendResultsGrid: FC<SendResultsGridProps> = ({
  result,
  ruleCheck,
  proxyRules = new Set<string>(),
  aiUsed = false,
  datasetFiles = 1,
  sectionNumber,
  testId = 'invivo-send-validator__results',
}) => {
  const { findings, counts, advisory, row_count: rowCount } = result;
  const datasetName = sendDatasetOf(result);
  const severityCounts = severityCountsOf(result);
  // Only a published grading's rejections block, and none exists for SEND in M1.
  const blocking = severityCounts?.reject ?? 0;
  // A rule can fail for a file with the finding reported beside another dataset's, as a cross-domain
  // check does, so the file's own findings alone cannot say it had no issues.
  const rulesFailed = ruleCheck?.failed ?? 0;
  // What the checks found, not what the result carries: a capped file must not understate itself here.
  const foundCount = counts.finding_groups;
  const undecidedChecks = counts.undecided_checks;
  // Every AI note, shown or not. They are advisory, so they never turn the file into a failure, but a
  // success headline must still say they are there to read.
  const aiNotes = advisory.observations;

  const parts = useMemo(() => partitionFindings(findings), [findings]);
  const sortedChecks = useMemo(() => [...parts.checks].sort(bySeverityThenRow), [parts.checks]);
  const disclosures = useMemo(
    () =>
      parts.disclosures.reduce<Array<SendDisclosure>>((listed, finding, index) => {
        if (_notNil(finding.disclosure)) {
          listed.push({
            key: `${finding.rule?.id ?? ''}-${finding.column_name ?? ''}-${index}`,
            reason: finding.disclosure,
            message: finding.message,
            rule: finding.rule,
            where: locationOf(finding),
          });
        }
        return listed;
      }, []),
    [parts.disclosures]
  );

  const [severities, setSeverities] = useState<ReadonlySet<string>>(() => new Set());
  const shownChecks = useMemo(
    () =>
      severities.size === 0 ? sortedChecks : sortedChecks.filter((finding) => severities.has(severityLevelOf(finding))),
    [sortedChecks, severities]
  );
  const severityChips = useMemo(() => severityChipsOf(severityCounts, sortedChecks), [severityCounts, sortedChecks]);
  const hasChecks = _isNotEmpty(sortedChecks);

  // AI notes are counted in their own section; this one announces only the checks' own omissions.
  const omission = { ...omissionOf(counts, advisory), aiNotes: 0, aiNotesNotShown: 0 };

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendResultsGrid"
      data-test-element="container"
      data-testid={testId}
    >
      <SendSectionHeader
        as="h3"
        number={sectionNumber}
        title={
          <>
            {sendDatasetLabel(datasetName)}{' '}
            <span className={styles.sectionMeta}>· {plural(rowCount, 'row', 'rows')}</span>
          </>
        }
        severityCounts={severityCounts}
        exactPass={ruleCheck?.exactPass === true}
        titleTestId="invivo-send-validator__results-meta"
      />

      <p className={styles.sectionCounts} data-test-element="server-counts">
        <span data-test-element="count-findings">{plural(foundCount, 'finding', 'findings')}</span>
        {undecidedChecks > 0 && (
          <span data-test-element="count-undecided">
            {' · '}
            {plural(undecidedChecks, "check couldn't decide", "checks couldn't decide")}
          </span>
        )}
        {aiNotes > 0 && (
          <span data-test-element="count-ai-notes">
            {' · '}
            {aiNotesCount(aiNotes)}, shown in their own section
          </span>
        )}
      </p>

      {blocking > 0 ? (
        <Banner critical dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {blocking} submission-blocking {blocking === 1 ? 'issue' : 'issues'} found
          </p>
        </Banner>
      ) : foundCount > 0 ? (
        <Banner warning dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            No submission-blocking issues — {foundCount} non-blocking {foundCount === 1 ? 'finding' : 'findings'} to
            review
          </p>
        </Banner>
      ) : rulesFailed > 0 ? (
        <Banner warning dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {datasetFiles > 1
              ? `${plural(rulesFailed, 'rule', 'rules')} failed for ${sendDatasetLabel(datasetName)} — this dataset is in ${datasetFiles} files, so the findings are in another of them or in the cross-domain checks`
              : `${plural(rulesFailed, 'rule', 'rules')} failed for ${sendDatasetLabel(datasetName)} — the findings are in the cross-domain checks`}
          </p>
        </Banner>
      ) : (
        <Banner
          success={ruleCheck?.exactPass === true}
          info={ruleCheck?.exactPass !== true}
          dismiss={false}
          testId="invivo-send-validator__results-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {noIssuesHeadline(datasetName, aiNotes, ruleCheck)}
          </p>
        </Banner>
      )}

      <SendOmissionNotice omission={omission} testId="invivo-send-validator__omission" />

      {hasChecks && (
        <div className="mt3">
          <SendFilterChips
            label={`Filter ${sendDatasetLabel(datasetName)} findings by severity`}
            options={severityChips}
            selected={severities}
            onToggle={(key) => setSeverities((current) => toggled(current, key))}
            testId="invivo-send-validator__severity-filter"
          />
          <div className="mt2">
            {_isNotEmpty(shownChecks) ? (
              <SendFindingsTable
                // A new filter starts at the first page.
                key={[...severities].sort().join(',')}
                findings={shownChecks}
                proxyRules={proxyRules}
                aiUsed={aiUsed}
                testId="invivo-send-validator__grid"
              />
            ) : (
              <p className="f6 dark-gray ma0" data-test-element="filter-empty">
                None of the findings shown here has the selected severity.
              </p>
            )}
          </div>
        </div>
      )}

      <SendDisclosureList
        disclosures={disclosures}
        undecidedChecks={undecidedChecks}
        testId="invivo-send-validator__disclosures"
      />
    </div>
  );
};

export default SendResultsGrid;
